import { z } from 'zod';

import { CreditCardSource, DocumentType } from './generated/enums.js';

// Note: `DocumentType` is generated from the Prisma enum (DB source of truth).

/**
 * Party role in a document transaction.
 */
export type PartyRole = 'VENDOR' | 'CUSTOMER';

// ============================================================================
// Document Types (Expanded)
// ============================================================================

/**
 * Expanded document types supporting multiple Brazilian fiscal documents,
 * tax payment slips, and receipts.
 */
export const DocumentTypeEnum = z.nativeEnum(DocumentType);
export type DocumentExtractionType = z.infer<typeof DocumentTypeEnum>;

// ============================================================================
// Classification Schemas (AI-Driven)
// ============================================================================

/**
 * Bill archetype classification for approval workflow routing.
 */
export const BillArchetypeEnum = z.enum([
  'FISCAL_FIRST',
  'PAYMENT_FIRST',
  'STATUTORY',
  'CONTRACTUAL_RECURRING',
  'REIMBURSEMENT',
  'EXCEPTION',
]);
export type BillArchetype = z.infer<typeof BillArchetypeEnum>;

/**
 * Classification with reasoning for archetype determination.
 */
export const ClassificationSchema = z.object({
  suggestedArchetype: BillArchetypeEnum,
  archetypeConfidence: z.number().min(0).max(100),
  archetypeReasoning: z.string(),
});
export type Classification = z.infer<typeof ClassificationSchema>;

/**
 * Vendor tax regime in Brazil.
 */
export const VendorTaxRegimeEnum = z.enum([
  'SIMPLES_NACIONAL',
  'LUCRO_PRESUMIDO',
  'LUCRO_REAL',
  'MEI',
]);
export type VendorTaxRegime = z.infer<typeof VendorTaxRegimeEnum>;

/**
 * AI-inferred flags for bill processing.
 */
export const InferredFlagsSchema = z.object({
  isRecurring: z.boolean(),
  recurringConfidence: z.number().min(0).max(100),
  recurringReasoning: z.string().nullable(),
  isReimbursement: z.boolean(),
  reimbursementConfidence: z.number().min(0).max(100),
  reimbursementReasoning: z.string().nullable(),
  isAlreadyPaid: z.boolean(),
  alreadyPaidConfidence: z.number().min(0).max(100),
  alreadyPaidReasoning: z.string().nullable(),
  isAutomaticBankPayment: z.boolean().nullable().default(null),
  automaticBankPaymentConfidence: z.number().min(0).max(100).nullable().default(null),
  automaticBankPaymentReasoning: z.string().nullable().default(null),
  /** Last 4 digits of the card used for payment when visible on the document (structured hint). */
  paymentCardLastFour: z.string().nullable(),
  isUrgent: z.boolean(),
  urgentReasoning: z.string().nullable(),
  vendorTaxRegime: VendorTaxRegimeEnum.nullable(),
  taxRegimeConfidence: z.number().min(0).max(100).nullable(),
  /**
   * "Possível ativo imobilizado?" — read from the document's own content, independent of
   * the GL account an analyst picked and of whether the NF-e carried an NCM. It SIGNALS,
   * it never decides: the deterministic engine
   * (`evaluateBillLineForCapitalization`) still owns asset-vs-expense, the class and the
   * capitalisation floor. Null means "no signal was produced", never "not an asset".
   *
   * NOTE (structured outputs): `.nullable().default(null)` keeps the field inside the
   * JSON Schema `required` array — which OpenAI strict mode demands — while leaving it
   * optional on the TS input side. `.optional()` would be dropped from `required` and
   * 400 the whole extraction for every document type.
   */
  isPossibleFixedAsset: z.boolean().nullable().default(null),
  fixedAssetConfidence: z.number().min(0).max(100).nullable().default(null),
  fixedAssetReasoning: z.string().nullable().default(null),
});
export type InferredFlags = z.infer<typeof InferredFlagsSchema>;

/**
 * Minimum confidence (0-100) required to treat an AI-inferred recurring flag as
 * authoritative for persistence/automation. Below this, `isRecurring` is treated
 * as a low-confidence hint only (mirrors the document-validator INFO threshold).
 */
export const RECURRING_INFERENCE_MIN_CONFIDENCE = 70;

/**
 * Returns true only when the inferred recurring flag is set AND its confidence
 * meets {@link RECURRING_INFERENCE_MIN_CONFIDENCE}. Use this at every persistence /
 * propagation chokepoint instead of reading `inferredFlags.isRecurring` directly,
 * so low-confidence guesses don't silently flip bills to recurring. A missing
 * confidence is treated as not-confident (conservative — never auto-flips).
 *
 * Note: this gate only governs AI-inferred recurrence. Deterministic recurrence
 * (e.g. statutory guias) is decided by domain handlers and does not pass through here.
 */
export function isConfidentRecurring(
  flags: Pick<InferredFlags, 'isRecurring' | 'recurringConfidence'> | null | undefined,
): boolean {
  if (!flags?.isRecurring) return false;
  return (flags.recurringConfidence ?? 0) >= RECURRING_INFERENCE_MIN_CONFIDENCE;
}

/**
 * Minimum confidence (0-100) required to let the "possível ativo imobilizado?" signal
 * reach the asset review queue.
 *
 * Deliberately lower than {@link RECURRING_INFERENCE_MIN_CONFIDENCE}: the two flags carry
 * asymmetric costs. A wrong recurring flag silently changes how a bill is scheduled; a
 * missed asset is silently expensed and never depreciates, while a false positive costs a
 * reviewer one glance at a queue row. So this one leans toward flagging.
 */
export const FIXED_ASSET_SIGNAL_MIN_CONFIDENCE = 50;

/**
 * Returns true only when the fixed-asset signal is set AND its confidence clears
 * {@link FIXED_ASSET_SIGNAL_MIN_CONFIDENCE}. Use this at every persistence /
 * candidate-generation chokepoint rather than reading `isPossibleFixedAsset` directly.
 *
 * A missing confidence is treated as not-confident — the same conservative reading
 * {@link isConfidentRecurring} takes.
 */
export function isConfidentPossibleFixedAsset(
  flags:
    | Partial<Pick<InferredFlags, 'isPossibleFixedAsset' | 'fixedAssetConfidence'>>
    | null
    | undefined,
): boolean {
  if (!flags?.isPossibleFixedAsset) return false;
  return (flags.fixedAssetConfidence ?? 0) >= FIXED_ASSET_SIGNAL_MIN_CONFIDENCE;
}

/**
 * Category suggestion for automatic classification.
 * Categories are fetched from ApSegment (name='Categoria') + ApSegmentValue at runtime.
 */
export const DocumentCategorySuggestionSchema = z.object({
  category: z.string(),
  categoryConfidence: z.number().min(0).max(100),
  subcategory: z.string().nullable(),
  suggestedCostCenterHint: z.string().nullable(),
});
export type DocumentCategorySuggestion = z.infer<typeof DocumentCategorySuggestionSchema>;

/**
 * Helper schema for mapping to ApBill fields.
 */
export const BillDataSchema = z.object({
  issueDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  documentNumber: z.string().nullable(),
  description: z.string(),
});
export type BillData = z.infer<typeof BillDataSchema>;

// ============================================================================
// Basic Entities
// ============================================================================

/**
 * Brazilian address structure.
 * Note: Some fields are optional to handle AI extraction scenarios where
 * not all address components may be present in the document.
 * Optional fields may be null when coming from AI extraction (OpenAI strict mode).
 */
export interface Address {
  street: string;
  number?: string | null; // May be "s/n" (sem número) or missing
  complement?: string | null;
  neighborhood?: string | null; // Not always present in documents
  city: string;
  state: string;
  zipCode?: string | null; // Not always present, especially in older documents
}

/**
 * Party (vendor or customer) in a document.
 */
export interface Party {
  role: PartyRole;
  legalName: string;
  tradeName?: string | null;
  taxId?: string | null;
  taxIdType?: string | null;
  isForeignVendor?: boolean | null;
  /** ISO 3166-1 alpha-2; read by vendor provisioning to scope foreign tax ids by country. */
  countryCode?: string | null;
  taxIdValid?: boolean | null;
  address?: Address | null;
  phone?: string | null;
  email?: string | null;
  identificationConfidence: number;
  stateRegistration?: string | null;
  municipalRegistration?: string | null;
}

/**
 * Tax applied to a line item or document.
 */
export interface Tax {
  taxType: string;
  calculationBase: number;
  rate: number;
  amount: number;
}

/**
 * Individual line item in an invoice.
 */
export interface LineItem {
  sequenceNumber: number;
  description: string;
  lineItemType?: 'SERVICE' | 'INTEREST' | 'FINE' | 'DISCOUNT' | 'OTHER';
  productCode?: string;
  cfopCode?: string;
  ncmCode?: string;
  cstCode?: string;
  quantity: number;
  unitOfMeasure?: string;
  unitPrice: number;
  grossValue: number;
  discount: number;
  freight?: number;
  insurance?: number;
  netValue: number;
  taxes?: Tax[];
}

/**
 * Tax breakdown at document level.
 */
export interface TaxBreakdown {
  taxType: string;
  calculationBase: number;
  rate: number;
  amount: number;
}

/**
 * Withholdings (tax retentions at source).
 */
export interface Withholdings {
  isRetained?: boolean;
  irrfAmount: number;
  issAmount: number;
  /**
   * IVA retenido — Mexican value-added tax withheld at source.
   *
   * Its own slot because it was being recorded as ISS, which is a Brazilian
   * municipal service tax and an unrelated liability: the payable looked right
   * in total while naming the wrong tax to the wrong authority. Optional so the
   * many places that build a Brazilian withholding set stay valid.
   */
  ivaRetainedAmount?: number;
  pisAmount: number;
  cofinsAmount: number;
  csllAmount: number;
  inssAmount: number;
  totalWithholdings: number;
}

/**
 * Financial summary of a document.
 */
export interface Financials {
  grossAmount?: number;
  taxBreakdown?: TaxBreakdown[];
  discounts: number;
  netAmount: number;
  currency: string;
  withholdings?: Withholdings;
  fineAmount?: number;
  interestAmount?: number;
  /**
   * The exchange rate the document itself states (e.g. "1 USD = 5.08604368618 BRL" printed on
   * an AWS invoice) — the rate the vendor actually used to charge the converted amount, not a
   * PTAX lookup. `documentExchangeRateFromCurrency`/`documentExchangeRateToCurrency` name what
   * it converts FROM/TO; a consumer must verify FROM matches `financials.currency` and TO
   * matches the entity's resolved functional currency before treating it as a rate — an
   * extraction that only checked the target could apply an EUR->BRL rate to a USD invoice, or a
   * hallucinated pairing, as if it were the correct basis (never assume BRL — see
   * currency-policy/AGENTS.md). Present only for document types whose extractor captures it
   * (currently INTERNATIONAL_INVOICE, ALC-1862 follow-up).
   */
  documentExchangeRate: number | null;
  documentExchangeRateFromCurrency: string | null;
  documentExchangeRateToCurrency: string | null;
  /**
   * The currency the extractor thinks a bare "$" stands for ("USD" or "MXN"), read from the
   * issuer's address, bank or tax ID. `currency` keeps "$". The currency policy books USD when the
   * issuer's own address is in the US; otherwise the bill goes to currency review and the review
   * dialog starts with this currency chosen. Null when `currency` is not "$" or the issuer shows
   * no evidence.
   */
  currencySuggestion?: string | null;
}

/**
 * Boleto-specific data (Brazilian payment slip).
 */
export interface BoletoSacadorAvalista {
  legalName: string;
  taxId?: string | null;
  taxIdType?: string | null;
}

/**
 * Sacador/Avalista data shown on some boletos.
 * Represents the original creditor/guarantor behind the charge.
 */
export const BoletoSacadorAvalistaSchema = z.object({
  legalName: z.string(),
  taxId: z.string().nullable(),
  taxIdType: z.string().nullable(),
});

/**
 * Boleto-specific data (Brazilian payment slip).
 */
export interface BoletoData {
  digitavelLine?: string;
  digitavelLineValid?: boolean;
  barcode?: string;
  bankCode?: string;
  bankName?: string;
  branchCode?: string;
  accountNumber?: string;
  nossoNumero?: string;
  documentNumber?: string;
  originalValue: number;
  finalValue: number;
  dueDate?: string | null;
  documentDate?: string;
  processingDate?: string;
  invoiceReference?: string;
  discountAmount?: number;
  discountDate?: string;
  interestRate?: number;
  finePercent?: number;
  /**
   * Pre-printed fine amount that would apply if boleto is paid after due date.
   * This is informational only — NOT included in financials.fineAmount or netAmount.
   * Used later during bank reconciliation to explain value differences.
   */
  conditionalFineAmount?: number;
  /**
   * Pre-printed interest amount that would apply if boleto is paid after due date.
   * This is informational only — NOT included in financials.interestAmount or netAmount.
   * Used later during bank reconciliation to explain value differences.
   */
  conditionalInterestAmount?: number;
  pixCopiaECola?: string;
  pixQrCodePresent?: boolean;
  sacadorAvalista?: BoletoSacadorAvalista | null;
}

/**
 * Additional information from NFe fiscal notes.
 */
export interface NfeAdditionalInformation {
  fiscalNotes?: string;
  legalReferences?: string[];
}

/**
 * Payment/banking info extracted from NF complementary information.
 * Used to auto-create payments when a bill is created from an NF.
 */
export interface NfPaymentInfo {
  dueDate?: string;
  bankCode?: string;
  bankName?: string;
  agency?: string;
  agencyDigit?: string;
  accountNumber?: string;
  accountDigit?: string;
  accountHolderName?: string;
  pixKey?: string;
  pixKeyType?: 'CNPJ' | 'CPF' | 'EMAIL' | 'PHONE' | 'RANDOM';
}

/** Declared ISS retention responsibility; UNKNOWN never implies the bill's entity. */
export const IssRetentionResponsibility = {
  NOT_WITHHELD: 'NOT_WITHHELD',
  CUSTOMER: 'CUSTOMER',
  INTERMEDIARY: 'INTERMEDIARY',
  UNKNOWN: 'UNKNOWN',
} as const;
export type IssRetentionResponsibility =
  (typeof IssRetentionResponsibility)[keyof typeof IssRetentionResponsibility];

/** NFe-specific data (Brazilian electronic invoice). */
export interface NfeData {
  accessKey?: string;
  accessKeyValid?: boolean;
  series?: string;
  number?: string;
  issueDateTime?: string;
  operationType?: string;
  fiscalOperationNature?: string;
  nfeModel?: string;
  verificationCode?: string;
  authorizationDate?: string;
  authorizationProtocol?: string;
  cfopCode?: string;
  serviceCode?: string;
  serviceDescription?: string;
  /**
   * LC 116/2003 fiscal service item code (e.g. "17.01", "33.01") inferred from the
   * service code/description. Used to auto-resolve the bill's fiscal service type and
   * to calculate tax withholdings during AP intake.
   */
  lc116Code?: string;
  /** Printed ISS amount, independent from whether the ISS is retained. */
  issAmount?: number;
  /**
   * Whether the NFS-e explicitly indicates ISS retention, independently of who retains it.
   * false = explicitly not retained; undefined = no evidence. Neither adds a withholding.
   */
  issWithheld?: boolean;
  /** Document-declared responsibility, not an inferred liability of the bill's entity.
   * Optional for historical extractions; missing must be treated as UNKNOWN, never CUSTOMER.
   */
  issRetentionResponsibility?: IssRetentionResponsibility;
  municipalityCode?: string;
  observations?: string;
  taxRegimeNotes?: string;
  additionalInformation?: NfeAdditionalInformation;
}

/**
 * Validation status for a field or document.
 */
export type ValidationStatus = 'PASS' | 'FAIL' | 'WARNING';

/**
 * Type of validation performed.
 */
export type ValidationType =
  | 'REQUIRED_FIELDS'
  | 'CNPJ_FORMAT'
  | 'CPF_FORMAT'
  | 'BOLETO_DIGITAVEL'
  | 'NFE_KEY_FORMAT'
  | 'NFE_CHAVE_ACESSO'
  | 'TOTAL_RECONCILIATION'
  | 'DATE_FORMAT'
  | 'TAX_CALCULATION'
  | 'DARF_BARCODE'
  | 'CFE_KEY_FORMAT';

/**
 * Result of a validation check.
 */
export interface ValidationResult {
  validationType: ValidationType;
  status: ValidationStatus;
  message: string;
  affectedFields: string[];
}

/**
 * Severity of a quality flag.
 */
export type QualityFlagSeverity = 'INFO' | 'WARNING' | 'ERROR';

/**
 * Type of quality issue.
 */
export type QualityFlagType =
  | 'LOW_CONFIDENCE'
  | 'MISSING_FIELD'
  | 'INCONSISTENT_DATA'
  | 'RECONCILIATION_MISMATCH'
  | 'CHECKSUM_MISMATCH'
  | 'DATE_INCONSISTENCY'
  | 'TAX_SLOT_UNCERTAIN'
  | 'SUGGESTED_TAX_SLOT_UNCERTAIN'
  | 'CFDI_UNMAPPED_RETENTION'
  | 'CFDI_UNREADABLE_AMOUNT';

/**
 * Quality flag indicating potential issues with extracted data.
 */
export interface QualityFlag {
  flagType: QualityFlagType;
  severity: QualityFlagSeverity;
  message: string;
  affectedField: string;
  confidence?: number;
}

/**
 * Primary source of document extraction data.
 */
export type PrimarySource = 'official' | 'ai';

// ============================================================================
// Flow-Specific Document Extraction Types
// ============================================================================

/**
 * Base fields common to all document extractions
 */
export type PaymentLineRefusalCode =
  'MISSING' | 'INVALID_CHECKSUM' | 'UNSUPPORTED_LENGTH' | 'VALUE_MISMATCH';

/** One payment-line candidate the gate refused. Scalars only: the raw digits are never kept. */
export interface PaymentLineRefusalCandidate {
  source: 'typed.barcode' | 'boletoData.barcode' | 'boletoData.digitavelLine';
  length: number;
  code: PaymentLineRefusalCode;
  encodedCents?: number;
  expectedCents?: number;
}

/**
 * Why the worker dropped the payment line after the retry, so no payment was scheduled.
 * Absent when the line was valid or the document had none.
 */
export interface PaymentLineRefusal {
  code: PaymentLineRefusalCode;
  kind: 'boleto' | 'statutory';
  attempts: number;
  candidates: PaymentLineRefusalCandidate[];
}

interface BaseDocumentExtraction {
  documentType: DocumentExtractionType;
  documentTypeConfidence: number;
  competencyDate?: string;
  guiaSubtype?: string;
  suggestedTaxSlot?: string;
  suggestedTaxSlotConfidence?: number;
  validationResults?: ValidationResult[] | null;
  qualityFlags?: QualityFlag[] | null;
  primarySource?: PrimarySource;
  paymentLineRefusal?: PaymentLineRefusal;
}

/**
 * Document extraction for bill flow
 * Contains fields specific to bills, invoices, and payment documents
 */
export interface BillDocumentExtraction extends BaseDocumentExtraction {
  documentType: DocumentExtractionType;

  // AI classification (bill-specific)
  classification?: Classification;
  billData?: BillData;

  // Parties (vendor/customer)
  parties: {
    vendor?: Party;
    customer?: Party;
  };

  // Line items and financials
  lineItems: LineItem[];
  financials: Financials;

  // Document-specific data (bill types)
  boletoData?: BoletoData;
  nfeData?: NfeData;
  darfData?: DarfData;
  gpsData?: GpsData;
  grfData?: GrfData;
  dasData?: DasData;
  guiaIssData?: GuiaIssData;
  guiaIcmsData?: GuiaIcmsData;
  cupomFiscalData?: CupomFiscalData;
  platformReceiptData?: PlatformReceiptData;
  cardInvoiceData?: CardInvoiceData;

  // Payment info from NF complementary information
  paymentInfo?: NfPaymentInfo;

  // AI inferences (bill-specific)
  inferredFlags?: InferredFlags;
  categorySuggestion?: DocumentCategorySuggestion;

  // Index signature for backward compatibility
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Document extraction for reconciliation flow
 * Contains fields specific to bank and card statements
 */
export interface ReconciliationDocumentExtraction extends BaseDocumentExtraction {
  documentType: 'BANK_STATEMENT' | 'CARD_STATEMENT' | 'OFX';

  // Reconciliation-specific data
  bankStatementData?: BankStatementData;
  cardStatementData?: CardStatementData;
  cardStatements?: CardStatementData[]; // Support for multiple cards in one document

  // Index signature for backward compatibility
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Union type for all document extraction types
 * Maintains backward compatibility with existing code
 *
 * Note: Prefer using specific types (BillDocumentExtraction, ReconciliationDocumentExtraction)
 * when working within a specific flow context for better type safety.
 */
export type DocumentExtraction = BillDocumentExtraction | ReconciliationDocumentExtraction;

// ============================================================================
// Zod Schemas for Runtime Validation and Structured Outputs
// ============================================================================

/**
 * Zod schema for Address
 * Note: Optional fields use .nullable() (not .nullish()) so that when converted to
 * JSON Schema for OpenAI Structured Outputs, every key in properties is in required.
 * OpenAI strict mode requires required to include every key in properties.
 */
export const AddressSchema = z.object({
  street: z.string(),
  number: z.string().nullable(), // May be "s/n" (sem número) or missing
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(), // Not always present in documents
  city: z.string(),
  state: z.string(),
  zipCode: z.string().nullable(), // Not always present, especially in older documents
});

/**
 * Zod schema for Party
 */
export const PartySchema = z.object({
  role: z.enum(['VENDOR', 'CUSTOMER']),
  legalName: z.string(),
  tradeName: z.string().nullable(),
  taxId: z.string().nullable(),
  taxIdType: z.string().nullable(),
  isForeignVendor: z.boolean().nullable(),
  taxIdValid: z.boolean().nullable(),
  address: AddressSchema.nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  identificationConfidence: z.number().min(0).max(100),
  stateRegistration: z.string().nullable(),
  municipalRegistration: z.string().nullable(),
});

/**
 * Zod schema for Tax
 */
export const TaxSchema = z.object({
  taxType: z.string(),
  calculationBase: z.number(),
  rate: z.number(),
  amount: z.number(),
});

/**
 * Zod schema for LineItem
 */
export const LineItemSchema = z.object({
  sequenceNumber: z.number(),
  description: z.string(),
  lineItemType: z.enum(['SERVICE', 'INTEREST', 'FINE', 'DISCOUNT', 'OTHER']).nullable(),
  productCode: z.string().nullable(),
  cfopCode: z.string().nullable(),
  ncmCode: z.string().nullable(),
  cstCode: z.string().nullable(),
  quantity: z.number(),
  unitOfMeasure: z.string().nullable(),
  unitPrice: z.number(),
  grossValue: z.number(),
  discount: z.number(),
  freight: z.number().nullable(),
  insurance: z.number().nullable(),
  netValue: z.number(),
  taxes: z.array(TaxSchema).nullable(),
});

/**
 * Zod schema for TaxBreakdown
 */
export const TaxBreakdownSchema = z.object({
  taxType: z.string(),
  calculationBase: z.number(),
  rate: z.number(),
  amount: z.number(),
});

/**
 * Zod schema for Withholdings
 */
export const WithholdingsSchema = z.object({
  isRetained: z.boolean().nullable(),
  irrfAmount: z.number(),
  issAmount: z.number(),
  pisAmount: z.number(),
  cofinsAmount: z.number(),
  csllAmount: z.number(),
  inssAmount: z.number(),
  totalWithholdings: z.number(),
});

/**
 * Zod schema for Financials
 */
export const FinancialsSchema = z.object({
  grossAmount: z.number().nullable(),
  taxBreakdown: z.array(TaxBreakdownSchema).nullable(),
  discounts: z.number(),
  netAmount: z.number(),
  currency: z.string(),
  withholdings: WithholdingsSchema.nullable(),
  fineAmount: z.number().nullable(),
  interestAmount: z.number().nullable(),
  documentExchangeRate: z.number().nullable(),
  documentExchangeRateFromCurrency: z.string().nullable(),
  documentExchangeRateToCurrency: z.string().nullable(),
  // `.nullable().default(null)` keeps it in the strict-mode `required` array while leaving it
  // optional on the TS input side.
  currencySuggestion: z.string().nullable().default(null),
});

/**
 * Zod schema for BoletoData
 */
export const BoletoDataSchema = z.object({
  digitavelLine: z.string().nullable(),
  digitavelLineValid: z.boolean().nullable(),
  barcode: z.string().nullable(),
  bankCode: z.string().nullable(),
  bankName: z.string().nullable(),
  branchCode: z.string().nullable(),
  accountNumber: z.string().nullable(),
  nossoNumero: z.string().nullable(),
  documentNumber: z.string().nullable(),
  originalValue: z.number(),
  finalValue: z.number(),
  dueDate: z.string().nullable(),
  documentDate: z.string().nullable(),
  processingDate: z.string().nullable(),
  invoiceReference: z.string().nullable(),
  discountAmount: z.number().nullable(),
  discountDate: z.string().nullable(),
  interestRate: z.number().nullable(),
  finePercent: z.number().nullable(),
  conditionalFineAmount: z.number().nullable(),
  conditionalInterestAmount: z.number().nullable(),
  pixCopiaECola: z.string().nullable(),
  pixQrCodePresent: z.boolean().nullable(),
  sacadorAvalista: BoletoSacadorAvalistaSchema.nullable(),
});

/**
 * Zod schema for NfeData
 */
export const NfeDataSchema = z.object({
  accessKey: z.string().nullable(),
  accessKeyValid: z.boolean().nullable(),
  series: z.string().nullable(),
  number: z.string().nullable(),
  issueDateTime: z.string().nullable(),
  operationType: z.string().nullable(),
  fiscalOperationNature: z.string().nullable(),
  nfeModel: z.string().nullable(),
  verificationCode: z.string().nullable(),
  authorizationDate: z.string().nullable(),
  authorizationProtocol: z.string().nullable(),
  cfopCode: z.string().nullable(),
  serviceCode: z.string().nullable(),
  serviceDescription: z.string().nullable(),
  lc116Code: z.string().nullable(),
  issAmount: z.number().nullable(),
  issWithheld: z.boolean().nullable(),
  issRetentionResponsibility: z.nativeEnum(IssRetentionResponsibility).nullable().default(null),
  municipalityCode: z.string().nullable(),
});

/**
 * Zod schema for NfPaymentInfo – payment/banking data from NF complementary information.
 */
export const NfPaymentInfoSchema = z.object({
  dueDate: z.string().nullable(),
  bankCode: z.string().nullable(),
  bankName: z.string().nullable(),
  agency: z.string().nullable(),
  agencyDigit: z.string().nullable(),
  accountNumber: z.string().nullable(),
  accountDigit: z.string().nullable(),
  accountHolderName: z.string().nullable(),
  pixKey: z.string().nullable(),
  pixKeyType: z.enum(['CNPJ', 'CPF', 'EMAIL', 'PHONE', 'RANDOM']).nullable(),
});

/**
 * Zod schema for DarfData
 */
export const DarfTaxLineSchema = z.object({
  taxCode: z.string(),
  taxDescription: z.string(),
  principalAmount: z.number(),
  fineAmount: z.number().nullable(),
  interestAmount: z.number().nullable(),
  totalAmount: z.number(),
});

export const DarfDataSchema = z.object({
  barcode: z.string().nullable(),
  barcodeValid: z.boolean().nullable(),
  taxCode: z.string().nullable(),
  taxDescription: z.string().nullable(),
  /** Explicit DCTFWeb contributor classification; absent on extractions created before ALC-3322. */
  isRpaContribuinteIndividual: z.boolean().nullable(),
  // Keep the extracted breakdown in the canonical AP schema. Zod strips unknown object keys by
  // default; omitting this field collapsed a multi-code DARF into one header-total tax line.
  taxLines: z.array(DarfTaxLineSchema).nullable(),
  dueDate: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  assessmentPeriod: z.string().nullable(),
  principalAmount: z.number(),
  fineAmount: z.number().nullable(),
  interestAmount: z.number().nullable(),
  totalAmount: z.number(),
  taxpayerTaxId: z.string().nullable(),
  taxpayerName: z.string().nullable(),
});
export type DarfData = z.infer<typeof DarfDataSchema>;

/**
 * Zod schema for GpsData
 */
export const GpsDataSchema = z.object({
  barcode: z.string().nullable(),
  paymentCode: z.string().nullable(),
  competencyPeriod: z.string().nullable(),
  identifierNumber: z.string().nullable(),
  principalAmount: z.number(),
  otherEntitiesAmount: z.number().nullable(),
  fineAmount: z.number().nullable(),
  interestAmount: z.number().nullable(),
  totalAmount: z.number(),
});
export type GpsData = z.infer<typeof GpsDataSchema>;

/**
 * Zod schema for GrfData
 */
export const GrfDataSchema = z.object({
  subtype: z.enum(['GRF', 'GRRF', 'GRDE']),
  competencyPeriod: z.string().nullable(),
  identifierCode: z.string().nullable(),
  totalAmount: z.number(),
  employeeCount: z.number().nullable(),
  barcode: z.string().nullable(),
});
export type GrfData = z.infer<typeof GrfDataSchema>;

/**
 * Zod schema for DasData
 */
export const DasDataSchema = z.object({
  competencyPeriod: z.string().nullable(),
  grossRevenue: z.number().nullable(),
  totalAmount: z.number(),
  barcode: z.string().nullable(),
  dasNumber: z.string().nullable(),
});
export type DasData = z.infer<typeof DasDataSchema>;

/**
 * Zod schema for GuiaIssData
 */
export const GuiaIssDataSchema = z.object({
  subtype: z.enum(['DAMSP', 'DARM', 'DUAM', 'DAM', 'NFS_ISS']),
  municipality: z.string(),
  municipalityCode: z.string().nullable(),
  /** Explicit RPA contributor classification from the municipal guide; never inferred from ISS retention. */
  isRpaContribuinteIndividual: z.boolean().nullable(),
  competencyPeriod: z.string().nullable(),
  serviceCode: z.string().nullable(),
  taxBase: z.number().nullable(),
  taxRate: z.number().nullable(),
  totalAmount: z.number(),
  barcode: z.string().nullable(),
  relatedNfseNumber: z.string().nullable(),
});
export type GuiaIssData = z.infer<typeof GuiaIssDataSchema>;

/**
 * Zod schema for GuiaIcmsData
 */
export const GuiaIcmsDataSchema = z.object({
  subtype: z.enum(['GNRE', 'GARE', 'DARE', 'DAE']),
  state: z.string().nullable(),
  competencyPeriod: z.string().nullable(),
  icmsType: z.enum(['PROPRIO', 'ST', 'DIFAL', 'IMPORTACAO']).nullable(),
  taxBase: z.number().nullable(),
  taxRate: z.number().nullable(),
  totalAmount: z.number(),
  barcode: z.string().nullable(),
  guiaNumber: z.string().nullable(),
});
export type GuiaIcmsData = z.infer<typeof GuiaIcmsDataSchema>;

/**
 * Zod schema for CupomFiscalData
 */
export const CupomFiscalDataSchema = z.object({
  satNumber: z.string().nullable(),
  cfeKey: z.string().nullable(),
  cfeKeyValid: z.boolean().nullable(),
  extractNumber: z.string().nullable(),
  issueDateTime: z.string().nullable(),
  approximateTaxPercent: z.number().nullable(),
  approximateTaxAmount: z.number().nullable(),
  paymentMethod: z.string().nullable(),
});
export type CupomFiscalData = z.infer<typeof CupomFiscalDataSchema>;

/**
 * Zod schema for PlatformReceiptData
 */
export const PlatformReceiptDataSchema = z.object({
  platform: z.enum(['AIRBNB', 'UBER', '99', 'IFOOD', 'RAPPI', 'OTHER']).nullable(),
  receiptId: z.string().nullable(),
  confirmationCode: z.string().nullable(),
  serviceDate: z.string().nullable(),
  serviceDateEnd: z.string().nullable(),
  basePrice: z.number().nullable(),
  serviceFees: z.number().nullable(),
  taxes: z.number().nullable(),
  discounts: z.number().nullable(),
  travelerName: z.string().nullable(),
  destination: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  paymentDate: z.string().nullable(),
  isPaid: z.boolean().nullable(),
});
export type PlatformReceiptData = z.infer<typeof PlatformReceiptDataSchema>;

export const CardInvoiceDetectedCardSchema = z.object({
  last4: z.string().nullable(),
  holderName: z.string().nullable(),
  brand: z.string().nullable(),
});

export const CardInvoiceDataSchema = z.object({
  issuerName: z.string().nullable(),
  issuerTaxId: z.string().nullable(),
  issuerBankCode: z.string().nullable(),
  providerHint: z.nativeEnum(CreditCardSource).nullable(),
  providerLabel: z.string().nullable(),
  statementLabel: z.string().nullable(),
  closingDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  totalAmount: z.number().nullable(),
  detectedCards: z.array(CardInvoiceDetectedCardSchema).nullable(),
  digitavelLine: z.string().nullable(),
  barcode: z.string().nullable(),
  pixQrCodePresent: z.boolean().nullable(),
  pixCopiaECola: z.string().nullable(),
});
export type CardInvoiceData = z.infer<typeof CardInvoiceDataSchema>;

// ============================================================================
// Bank Statement Data
// ============================================================================

/**
 * AI confidence scores for extracted transaction fields.
 * Used to track the reliability of AI-extracted data and enable
 * confidence-based decision trees when merging parser and AI data.
 */
export const TransactionAIConfidenceSchema = z.object({
  counterpartyName: z.number().min(0).max(100).nullish(), // Optional - not all transactions have counterparties
  counterpartyDocument: z.number().min(0).max(100).nullish(),
  paymentMethod: z.number().min(0).max(100).nullish(),
  referenceCode: z.number().min(0).max(100).nullish(),
  internalOperationType: z.number().min(0).max(100).nullish(), // For YIELD/RESCUE/APPLICATION classification
});
export type TransactionAIConfidence = z.infer<typeof TransactionAIConfidenceSchema>;

/**
 * Data source tracking for merged transaction fields.
 * Indicates whether each field came from the parser or AI enrichment.
 */
export const TransactionDataSourceSchema = z.object({
  counterpartyName: z.enum(['AI', 'PARSER']), // AI or deterministic/parser extraction
  paymentMethod: z.enum(['PARSER', 'AI']),
  referenceCode: z.enum(['PARSER', 'AI']).nullish(),
});
export type TransactionDataSource = z.infer<typeof TransactionDataSourceSchema>;

/**
 * Bank transaction data extracted from bank statements.
 * Maps directly to op_bank_transaction table.
 *
 * This schema supports a hybrid parser + AI flow where:
 * - Structured data (dates, amounts, balances) comes from parser
 * - descriptionRaw always comes from parser (original bank description)
 * - counterparty data always comes from AI extraction
 * - Other fields use confidence-based decision tree
 */
export const BankTransactionDataSchema = z.object({
  postedAt: z.string(), // ISO 8601 date string
  effectiveAt: z.string().nullish(), // ISO 8601 date string
  direction: z.enum(['DEBIT', 'CREDIT']),
  amount: z.number(),
  descriptionRaw: z.string(), // ALWAYS from parser - original bank description
  counterpartyName: z.string().nullish(), // ALWAYS from AI when available
  counterpartyDocument: z.string().nullish(), // ALWAYS from AI when available
  paymentMethod: z.enum([
    'PIX',
    'TED',
    'DOC',
    'BOLETO',
    'CARD',
    'DIRECT_DEBIT',
    'DIRECT_CREDIT',
    'SPEI',
    'UNKNOWN',
  ]),
  // Internal operation type for investment/internal operations (typically with PIX or TED)
  internalOperationType: z
    .enum([
      'YIELD', // Rendimentos (crédito puro)
      'RESCUE', // Resgate de aplicação
      'APPLICATION', // Aplicação
    ])
    .nullish(),
  referenceCode: z.string().nullish(), // PIX end-to-end, NSU, etc.
  /** Bank-provided unique transaction ID (OFX FITID, CSV Identificador da movimentação). Used for deterministic deduplication. */
  fitId: z.string().nullish(),
  balanceAfter: z.number().nullish(),

  // Parser + AI merge metadata
  aiConfidence: TransactionAIConfidenceSchema.nullish(), // AI confidence scores per field
  dataSource: TransactionDataSourceSchema.nullish(), // Source tracking per field
  warnings: z.array(z.string()).nullish(), // Warnings for low confidence or conflicts
  confidenceScore: z.number().min(0).max(100).nullish(), // Overall confidence score
});
export type BankTransactionData = z.infer<typeof BankTransactionDataSchema>;

/**
 * Bank statement data extracted from bank statements (OFX, CSV, PDF).
 * Maps directly to op_bank_statement table.
 */
export const BankStatementDataSchema = z.object({
  statement: z.object({
    periodStart: z.string(), // ISO 8601 date string
    periodEnd: z.string(), // ISO 8601 date string
    openingBalance: z.number(),
    closingBalance: z.number(),
    bankAccountId: z.string().nullish(), // UUID, if identifiable from statement
    bankAccountNumber: z.string().nullish(), // Account number for matching
    bankCode: z.string().nullish(), // Bank code for matching
    branchCode: z.string().nullish(), // Branch code for matching
    currencyCode: z.string().nullish(), // Exact OFX CURDEF when supplied by the statement
    providerStatementId: z.string().nullish(), // External provider statement ID
    ledgerBalanceFromOfx: z
      .object({ amount: z.number(), dateAsOf: z.string().optional() })
      .optional(), // For validation only: compare calculated closing vs bank-provided BALAMT (amount in currency units)
  }),
  transactions: z.array(BankTransactionDataSchema),
});
export type BankStatementData = z.infer<typeof BankStatementDataSchema>;

// ============================================================================
// Card Statement Data
// ============================================================================

/**
 * AI confidence scores for card statement item fields.
 */
export const CardItemAIConfidenceSchema = z.object({
  merchantName: z.number().min(0).max(100),
  merchantDocument: z.number().min(0).max(100).nullish(),
  mcc: z.number().min(0).max(100).nullish(),
});
export type CardItemAIConfidence = z.infer<typeof CardItemAIConfidenceSchema>;

/**
 * Data source tracking for merged card statement item fields.
 */
export const CardItemDataSourceSchema = z.object({
  merchantName: z.enum(['AI', 'PARSER']),
  mcc: z.enum(['PARSER', 'AI']).nullish(),
});
export type CardItemDataSource = z.infer<typeof CardItemDataSourceSchema>;

/**
 * Card statement item data extracted from card statements.
 * Maps directly to op_card_statement_item table.
 *
 * Similar to bank transactions, supports hybrid parser + AI flow:
 * - Structured data (dates, amounts) from parser
 * - descriptionRaw always from parser (original statement description)
 * - merchantName can be enriched by AI
 */
export const CardStatementItemDataSchema = z.object({
  postedAt: z.string(), // ISO 8601 date string
  amount: z.number(),
  merchantName: z.string(), // Can be enriched by AI
  descriptionRaw: z.string().nullish(), // ALWAYS from parser when available
  mcc: z.string().nullish(), // Merchant Category Code (4 digits)
  installmentNumber: z.number().nullish(), // 1, 2, 3...
  installmentTotal: z.number().nullish(), // Total number of installments
  iofAmount: z.number().nullish(),
  operatorFee: z.number().nullish(),

  foreignCurrencyAmount: z.number().nullish(),
  foreignCurrencyCode: z.string().nullish(), // USD, EUR, etc.
  rawLine: z.string().nullish(), // Full CSV line for audit/verification (stored in metadata)

  // Per-item card (from CSV "Cartão" column) - enables correct assignment when file has multiple cards
  cardLast4: z.string().nullish(),

  // Parser + AI merge metadata (similar to bank transactions)
  aiConfidence: CardItemAIConfidenceSchema.nullish(),
  dataSource: CardItemDataSourceSchema.nullish(),
  warnings: z.array(z.string()).nullish(),
  confidenceScore: z.number().min(0).max(100).nullish(),
});
export type CardStatementItemData = z.infer<typeof CardStatementItemDataSchema>;

/**
 * Card statement data extracted from card statements (CSV, PDF).
 * Maps directly to op_card_statement table.
 */
export const CardStatementDataSchema = z.object({
  statement: z.object({
    periodStart: z.string(), // ISO 8601 date string
    periodEnd: z.string(), // ISO 8601 date string
    closingDate: z.string(), // ISO 8601 date string - statement closing date
    dueDate: z.string(), // ISO 8601 date string - payment due date
    totalAmount: z.number(),
    cardAccountId: z.string().nullish(), // UUID, if identifiable from statement
    cardLast4: z.string().nullish(), // Last 4 digits for matching
    cardBrand: z.string().nullish(), // Card brand (VISA, MASTERCARD, etc.) for matching
    providerStatementId: z.string().nullish(), // External provider statement ID
    amountUnit: z.enum(['MINOR', 'MAJOR']).nullish(),
  }),
  items: z.array(CardStatementItemDataSchema),
});
export type CardStatementData = z.infer<typeof CardStatementDataSchema>;

// ============================================================================
// Reconciliation Completion (Parser + AI)
// ============================================================================

export const ReconciliationCompletionFileTypeSchema = z.enum(['OFX', 'CSV', 'PDF']);
export type ReconciliationCompletionFileType = z.infer<
  typeof ReconciliationCompletionFileTypeSchema
>;

export const ReconciliationCompletionInputSchema = z.object({
  documentType: z.enum(['BANK_STATEMENT', 'CARD_STATEMENT']),
  fileType: ReconciliationCompletionFileTypeSchema,
  rawText: z.string(),
  parsedBankStatementData: BankStatementDataSchema.nullish(),
  parsedCardStatementData: CardStatementDataSchema.nullish(),
});
export type ReconciliationCompletionInput = z.infer<typeof ReconciliationCompletionInputSchema>;

export const ReconciliationCompletionOutputSchema = z.object({
  completedBankStatementData: BankStatementDataSchema.nullish(),
  completedCardStatementData: CardStatementDataSchema.nullish(),
  missingFields: z.array(z.string()),
  confidenceByField: z.record(z.string(), z.number()).nullish(),
});
export type ReconciliationCompletionOutput = z.infer<typeof ReconciliationCompletionOutputSchema>;

/**
 * Zod schema for ValidationResult
 */
export const ValidationResultSchema = z.object({
  validationType: z.enum([
    'REQUIRED_FIELDS',
    'CNPJ_FORMAT',
    'CPF_FORMAT',
    'BOLETO_DIGITAVEL',
    'NFE_KEY_FORMAT',
    'NFE_CHAVE_ACESSO',
    'TOTAL_RECONCILIATION',
    'DATE_FORMAT',
    'TAX_CALCULATION',
    'DARF_BARCODE',
    'CFE_KEY_FORMAT',
  ]),
  status: z.enum(['PASS', 'FAIL', 'WARNING']),
  message: z.string(),
  affectedFields: z.array(z.string()),
});

/**
 * Zod schema for QualityFlag
 */
export const QualityFlagSchema = z.object({
  flagType: z.enum([
    'LOW_CONFIDENCE',
    'MISSING_FIELD',
    'INCONSISTENT_DATA',
    'RECONCILIATION_MISMATCH',
    'CHECKSUM_MISMATCH',
    'DATE_INCONSISTENCY',
  ]),
  severity: z.enum(['INFO', 'WARNING', 'ERROR']),
  message: z.string(),
  affectedField: z.string(),
  confidence: z.number().nullable(),
});

/**
 * Bill-only extraction schema used by AP bill flows.
 *
 * Important: keep this schema free of reconciliation-only fields like
 * `bankStatementData` and `cardStatementData`. OpenAI Structured Outputs in
 * strict mode requires every property in nested objects to be listed in
 * `required`, so including unrelated reconciliation schemas here can break bill
 * extraction even when those fields are never relevant for the prompt.
 */
export const BillDocumentExtractionSchema = z.object({
  // === CLASSIFICATION ===
  documentType: DocumentTypeEnum,
  documentTypeConfidence: z.number().min(0).max(100),
  competencyDate: z.string().nullable(),
  guiaSubtype: z.string().nullable(),
  suggestedTaxSlot: z.string().nullable(),
  suggestedTaxSlotConfidence: z.number().min(0).max(100).nullable(),

  // === NEW: ARCHETYPE CLASSIFICATION ===
  classification: ClassificationSchema,

  // === NEW: BILL DATA (for easier mapping) ===
  billData: BillDataSchema,

  // === PARTIES ===
  parties: z.object({
    vendor: PartySchema.nullable(),
    customer: PartySchema.nullable(),
  }),

  // === LINE ITEMS ===
  lineItems: z.array(LineItemSchema),

  // === FINANCIALS ===
  financials: FinancialsSchema,

  // === DOCUMENT-SPECIFIC DATA ===
  boletoData: BoletoDataSchema.nullable(),
  nfeData: NfeDataSchema.nullable(),
  darfData: DarfDataSchema.nullable(),
  gpsData: GpsDataSchema.nullable(),
  grfData: GrfDataSchema.nullable(),
  dasData: DasDataSchema.nullable(),
  guiaIssData: GuiaIssDataSchema.nullable(),
  guiaIcmsData: GuiaIcmsDataSchema.nullable(),
  cupomFiscalData: CupomFiscalDataSchema.nullable(),
  platformReceiptData: PlatformReceiptDataSchema.nullable(),
  cardInvoiceData: CardInvoiceDataSchema.nullable(),

  // === PAYMENT INFO (from NF complementary info) ===
  paymentInfo: NfPaymentInfoSchema.nullable(),

  // === NEW: INFERRED FLAGS ===
  inferredFlags: InferredFlagsSchema,

  // === NEW: CATEGORY SUGGESTION ===
  categorySuggestion: DocumentCategorySuggestionSchema,

  // === VALIDATION & QUALITY ===
  validationResults: z.array(ValidationResultSchema).nullable(),
  qualityFlags: z.array(QualityFlagSchema).nullable(),

  // === METADATA ===
  primarySource: z.enum(['official', 'ai']).nullable(),
});

/**
 * DARF tax codes (regulatory reference data).
 */
export const DARF_TAX_CODES: Record<string, string> = {
  '0190': 'IRRF - Rendimentos do Trabalho',
  '0561': 'IRRF - Rendimentos do Trabalho',
  '1708': 'IRRF - Remuneração Serviços PJ',
  '2089': 'IRPJ - PJ em Geral',
  '2172': 'COFINS - Faturamento',
  '2372': 'CSLL - PJ em Geral',
  '2484': 'COFINS - Não Cumulativo',
  '5952': 'PIS - Não Cumulativo',
  '6912': 'PIS - Não Cumulativo',
  '8109': 'PIS - Faturamento',
};

/**
 * Main Zod schema for DocumentExtraction
 *
 * This schema is used for:
 * - Runtime validation of AI extraction results
 * - Structured outputs from AI models (Anthropic Claude, Gemini)
 * - Type generation for TypeScript interfaces
 *
 * The schema supports multiple document types including:
 * - Payment slips: BOLETO, BOLETO_COM_NF
 * - Invoices: NFE_PRODUTO, NFE_SERVICO
 * - Tax documents: DARF, GPS, GUIA_ISS, GUIA_ICMS
 * - Receipts: CUPOM_FISCAL, RECIBO_PLATAFORMA, RECIBO_GENERICO, COMPROVANTE_PAGAMENTO
 *
 * New in this version:
 * - AI-driven archetype classification (FISCAL_FIRST, PAYMENT_FIRST, etc.)
 * - Inferred flags (recurring, reimbursement, already paid)
 * - Category suggestions for automatic bill categorization
 * - Bill data helper for easier mapping to ApBill
 */
export const DocumentExtractionSchema = z.object({
  // === CLASSIFICATION ===
  documentType: DocumentTypeEnum,
  documentTypeConfidence: z.number().min(0).max(100),
  competencyDate: z.string().nullable(),
  guiaSubtype: z.string().nullable(),
  suggestedTaxSlot: z.string().nullable(),
  suggestedTaxSlotConfidence: z.number().min(0).max(100).nullable(),

  // === NEW: ARCHETYPE CLASSIFICATION ===
  classification: ClassificationSchema,

  // === NEW: BILL DATA (for easier mapping) ===
  billData: BillDataSchema,

  // === PARTIES ===
  parties: z.object({
    vendor: PartySchema.nullable(),
    customer: PartySchema.nullable(),
  }),

  // === LINE ITEMS ===
  lineItems: z.array(LineItemSchema),

  // === FINANCIALS ===
  financials: FinancialsSchema,

  // === DOCUMENT-SPECIFIC DATA ===
  boletoData: BoletoDataSchema.nullable(),
  nfeData: NfeDataSchema.nullable(),
  darfData: DarfDataSchema.nullable(),
  gpsData: GpsDataSchema.nullable(),
  grfData: GrfDataSchema.nullable(),
  dasData: DasDataSchema.nullable(),
  guiaIssData: GuiaIssDataSchema.nullable(),
  guiaIcmsData: GuiaIcmsDataSchema.nullable(),
  cupomFiscalData: CupomFiscalDataSchema.nullable(),
  platformReceiptData: PlatformReceiptDataSchema.nullable(),
  cardInvoiceData: CardInvoiceDataSchema.nullable(),
  bankStatementData: BankStatementDataSchema.nullable(),
  cardStatementData: CardStatementDataSchema.nullable(),
  cardStatements: z.array(CardStatementDataSchema),

  // === PAYMENT INFO (from NF complementary info) ===
  paymentInfo: NfPaymentInfoSchema.nullable(),

  // === NEW: INFERRED FLAGS ===
  inferredFlags: InferredFlagsSchema,

  // === NEW: CATEGORY SUGGESTION ===
  categorySuggestion: DocumentCategorySuggestionSchema,

  // === VALIDATION & QUALITY ===
  validationResults: z.array(ValidationResultSchema).nullable(),
  qualityFlags: z.array(QualityFlagSchema).nullable(),

  // === METADATA ===
  primarySource: z.enum(['official', 'ai']).nullable(),
});

/**
 * Simplified schema for reconciliation documents (bank statements, card statements)
 * Only includes fields relevant to reconciliation flow
 *
 * This schema is MUCH smaller and simpler than DocumentExtractionSchema,
 * reducing API payload size and avoiding 403 errors with large PDFs
 */
export const ReconciliationDocumentExtractionSchema = z.object({
  // === CLASSIFICATION ===
  documentType: DocumentTypeEnum,
  documentTypeConfidence: z.number().min(0).max(100),

  // === RECONCILIATION-SPECIFIC DATA ===
  bankStatementData: BankStatementDataSchema.nullish(),
  cardStatementData: CardStatementDataSchema.nullish(),
  cardStatements: z.array(CardStatementDataSchema).nullish(),

  // === VALIDATION & QUALITY ===
  qualityFlags: z.array(QualityFlagSchema).nullish(),

  // === METADATA ===
  primarySource: z.enum(['official', 'ai']).nullish(),
});

/**
 * Schema for BANK_STATEMENT extraction only.
 * Requires bankStatementData so the model must return statement + transactions
 * (used for structured output to avoid classification-only responses on PDFs).
 */
export const BankStatementExtractionSchema = z.object({
  documentType: z.literal('BANK_STATEMENT'),
  documentTypeConfidence: z.number().min(0).max(100),
  bankStatementData: BankStatementDataSchema,
  qualityFlags: z.array(QualityFlagSchema).nullish(),
  primarySource: z.enum(['official', 'ai']).nullish(),
});

/**
 * Schema for CARD_STATEMENT extraction only.
 * cardStatements defaults to [] when the model omits it (avoids parse failure on
 * partial responses). The agent validates emptiness and throws a user-friendly error.
 */
export const CardStatementExtractionSchema = z.object({
  documentType: z.literal('CARD_STATEMENT'),
  documentTypeConfidence: z.number().min(0).max(100),
  cardStatements: z.array(CardStatementDataSchema).default([]),
  qualityFlags: z.array(QualityFlagSchema).nullish(),
  primarySource: z.enum(['official', 'ai']).nullish(),
});
